import EPopupAccordion from '@/components/everridge/popup/EPopupAccordion';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import { FinanceAnalyticsByMonthsResponseDto } from '../../types/finances-dto';
import AnalyticsElement from './AnalyticsElement';
import AnalyticsCardSkeleton from './AnalyticsCardSkeleton';
import CurrencyValue from '../CurrencyValue';
import DollarCircleIconSolid from '@/components/icons/DollarCircleIconSolid';
import MoneySendCircleIcon from '@/components/icons/MoneySendCircleIcon';
import ArrowReloadHorizontalIcon from '@/components/icons/ArrowReloadHorizontalIcon';
import MoneyNotFound02IconSolid from '@/components/icons/MoneyNotFound02IconSolid';

type Props = {
    data?: FinanceAnalyticsByMonthsResponseDto;
    isLoading?: boolean;
    /** When false, the expenses row is omitted (e.g. all rent analytics expense lines are hidden). */
    showExpensesSummary?: boolean;
};

const AnalyticsSummary: FC<Props> = ({ data, isLoading, showExpensesSummary = true }) => {
    const { t } = useTranslation();
    const summaryRowCount = showExpensesSummary ? 4 : 3;

    return (
        <div className="flex flex-col gap-4">
            {isLoading && (
                <>
                    {Array.from({ length: summaryRowCount }).map((_, index) => (
                        <AnalyticsCardSkeleton key={index} />
                    ))}
                </>
            )}
            {!isLoading && (
                <>
                    <EPopupAccordion
                        icon={DollarCircleIconSolid}
                        label={t(
                            'main.finances.components.analytics.components.summary.grossRevenue',
                        )}
                        value={<CurrencyValue value={data?.grossRevenue} asset='other' />}
                        content={<AnalyticsElement values={data?.grossRevenueMonths} />}
                        iconClassName="size-8 shrink-0 flex items-center justify-center rounded-semi-xl bg-success-100 fill-success-1000 stroke-none"
                    />
                    {showExpensesSummary && (
                        <EPopupAccordion
                            icon={MoneySendCircleIcon}
                            label={t('main.finances.components.analytics.components.summary.expenses')}
                            value={<CurrencyValue value={data?.expenses} asset='other' />}
                            content={<AnalyticsElement values={data?.expensesMonths} />}
                            iconClassName="size-8 shrink-0 flex items-center justify-center rounded-semi-xl bg-error-100 fill-error-1000 stroke-none"
                        />
                    )}
                    <EPopupAccordion
                        icon={MoneyNotFound02IconSolid}
                        label={t('main.finances.components.analytics.components.summary.deduction')}
                        value={<CurrencyValue value={data?.deduction} asset='other' />}
                        content={<AnalyticsElement values={data?.deductionMonths} />}
                        iconClassName="size-8 shrink-0 flex items-center justify-center rounded-semi-xl bg-info-100 fill-info-1000 stroke-none"
                    />
                    <EPopupAccordion
                        icon={ArrowReloadHorizontalIcon}
                        label={t(
                            'main.finances.components.analytics.components.summary.transferredFunds',
                        )}
                        value={<CurrencyValue value={data?.transferredFunds} asset='other' />}
                        content={<AnalyticsElement values={data?.transferredFundsMonths} />}
                        iconClassName="size-8 shrink-0 flex items-center justify-center rounded-semi-xl bg-warning-100 stroke-warning-1000 fill-none"
                    />
                </>
            )}
        </div>
    );
};

export default AnalyticsSummary;
